import { useEffect, useRef, useMemo, useCallback } from 'react';
import isEqual from 'lodash/isEqual';

export function useAcledHeatmap(filteredAcledData, selectedPillarKey, setMapLayers) {
  // Keep a reference to the latest data to compare for meaningful changes
  const previousDataRef = useRef(null);
  const previousLayerRef = useRef(null);
  const prevPillarKeyRef = useRef(null);
  const lastUpdateTimeRef = useRef(0);
  const isUpdatingRef = useRef(false);
  
  // Memoize the heatmap data to prevent unnecessary recalculations
  const heatmapData = useMemo(() => {
    if (!filteredAcledData?.length) return null;
    
    // Filter out any events without valid coordinates
    const validData = filteredAcledData.filter(event => 
      event.longitude && event.latitude && 
      !isNaN(parseFloat(event.longitude)) && 
      !isNaN(parseFloat(event.latitude))
    );
    
    if (validData.length === 0) return null;
    
    return validData.map(event => ({
      lon: parseFloat(event.longitude),
      lat: parseFloat(event.latitude),
      fatalities: parseInt(event.fatalities || 1, 10)
    }));
  }, [filteredAcledData]);
  
  // Memoize the max fatalities calculation
  const maxFatalities = useMemo(() => {
    if (!heatmapData) return 10;
    return Math.max(...heatmapData.map(d => d.fatalities), 10);
  }, [heatmapData]);
  
  // Memoize the layer configuration to prevent unnecessary recreations
  const acledHeatmapLayer = useMemo(() => {
    if (!heatmapData) return null;
    
    return {
      id: 'acled-heatmap', // Use a stable ID to prevent unnecessary redraws
      name: 'ACLED Events Heatmap',
      type: 'heatmap',
      data: heatmapData,
      zIndex: 1,
      opacity: 0.86,
      blur: 12,
      radius: 7,
      fillPalette: 'YlOrRd',
      weighted: true,
      scaleMax: maxFatalities
    };
  }, [heatmapData, maxFatalities]);
  
  // Determine if we should show the ACLED layer
  const shouldShowAcledLayer = useMemo(() => {
    return (selectedPillarKey === 'conflict' || 
            selectedPillarKey?.startsWith('conflict_')) && 
           acledHeatmapLayer !== null;
  }, [selectedPillarKey, acledHeatmapLayer]);
  
  // Memoize the layer update function
  const updateMapLayers = useCallback((prevLayers) => {
    // Remove all previous acled-heatmap layers
    const filteredLayers = prevLayers.filter(layer => layer.id !== 'acled-heatmap');
    
    // Only add the layer if we should show it
    return shouldShowAcledLayer ? [...filteredLayers, acledHeatmapLayer] : filteredLayers;
  }, [acledHeatmapLayer, shouldShowAcledLayer]);
  
  // Optimized effect - throttles updates when they happen frequently
  useEffect(() => {
    // Skip if we're already processing an update
    if (isUpdatingRef.current) return;
    
    // Check if critical data has actually changed to avoid unnecessary updates
    const hasDataChanged = !isEqual(heatmapData, previousDataRef.current);
    const hasPillarChanged = selectedPillarKey !== prevPillarKeyRef.current;
    
    if (hasDataChanged || hasPillarChanged) {
      const now = Date.now();
      const timeSinceLastUpdate = now - lastUpdateTimeRef.current;
      
      // Set flag to prevent concurrent updates
      isUpdatingRef.current = true;
      
      // If updates are happening rapidly (e.g., during slider drag), use requestAnimationFrame
      // to ensure smooth updates that don't block the main thread
      if (timeSinceLastUpdate < 100) {
        requestAnimationFrame(() => {
          setMapLayers(updateMapLayers);
          previousDataRef.current = heatmapData;
          previousLayerRef.current = acledHeatmapLayer;
          prevPillarKeyRef.current = selectedPillarKey;
          lastUpdateTimeRef.current = Date.now();
          isUpdatingRef.current = false;
        });
      } else {
        // For less frequent updates, apply immediately
        setMapLayers(updateMapLayers);
        previousDataRef.current = heatmapData;
        previousLayerRef.current = acledHeatmapLayer;
        prevPillarKeyRef.current = selectedPillarKey;
        lastUpdateTimeRef.current = now;
        isUpdatingRef.current = false;
      }
    }
  }, [heatmapData, selectedPillarKey, acledHeatmapLayer, updateMapLayers, setMapLayers]);
}